import { ButtonHTMLAttributes, ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost'
type Size    = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  children: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  children,
  disabled,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <>
      <button
        {...rest}
        disabled={disabled || loading}
        className={`btn btn--${variant} btn--${size} ${loading ? 'btn--loading' : ''} ${className}`}
      >
        {loading && <span className="btn-spinner" />}
        {!loading && icon && <span className="btn-icon">{icon}</span>}
        <span>{children}</span>
      </button>

      <style>{`
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: var(--radius-md);
          font-weight: 500;
          font-family: var(--font-sans);
          border: 1px solid transparent;
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s, opacity 0.15s;
          white-space: nowrap;
        }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        /* Sizes */
        .btn--sm { padding: 5px 12px; font-size: 0.78rem; }
        .btn--md { padding: 8px 16px;  font-size: 0.875rem; }
        .btn--lg { padding: 11px 22px; font-size: 0.95rem; }
        /* Primary */
        .btn--primary { background: var(--accent); color: #fff; border-color: var(--accent); }
        .btn--primary:hover:not(:disabled) { background: var(--accent-hover); border-color: var(--accent-hover); }
        /* Secondary */
        .btn--secondary { background: var(--bg-surface); color: var(--text-primary); border-color: var(--border); }
        .btn--secondary:hover:not(:disabled) { background: var(--bg-hover); }
        /* Danger */
        .btn--danger { background: var(--bg-error); color: var(--color-error); border-color: var(--color-error); }
        .btn--danger:hover:not(:disabled) { background: var(--color-error); color: #fff; }
        /* Ghost */
        .btn--ghost { background: transparent; color: var(--text-secondary); border-color: transparent; }
        .btn--ghost:hover:not(:disabled) { background: var(--bg-hover); color: var(--text-primary); }
        /* Spinner */
        .btn-spinner {
          width: 14px; height: 14px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          flex-shrink: 0;
        }
        .btn-icon { display: flex; align-items: center; }
      `}</style>
    </>
  )
}
